/**
 * DiffView — renders one GET /versions/:a/diff/:b result (contracts-plan.md
 * §11 tab 2, §13.8 WP-74; R34, G3, G7, G8, G9).
 *
 * Budget pair (`kind: 'budget'`): the line diff first (Changed / Added /
 * Removed, each row showing both values, the delta and the delta percent
 * with tabular-nums, and a `title=` carrying both provenance cells and the
 * G8 label), then the cell diff under a collapsed disclosure with the
 * truncation stated when present.
 *
 * Contract pair (`kind: 'contract'`): the clause table (path, heading,
 * added/removed/changed) plus a Download the Word redline button (WP-64's
 * GET /diff?a&b) showing the four header counts, refused shown as a warning
 * (refused paragraphs are unread things, G9).
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Download, AlertTriangle } from 'lucide-react';
import type { VersionDiffResult, BudgetLineDiffRow, RedlineDiffSummary } from '../../api/versions';

export interface DiffViewProps {
  result: VersionDiffResult;
  onDownloadRedline: () => void;
  redlineLoading: boolean;
  redlineError: string | null;
  redlineSummary: RedlineDiffSummary | null;
}

function money(value: number | null): string {
  if (value === null) return '—';
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(value: number | null): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function points(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value * 1000) / 10} points`;
}

function lineTitle(row: BudgetLineDiffRow, side: 'a' | 'b'): string {
  const p = row.provenance[side];
  const label = row.labels[side] ?? 'extracted';
  const locator = [p.sheet ? `sheet ${p.sheet}` : null, p.cell ? `cell ${p.cell}` : null].filter(Boolean).join(', ');
  return `${side.toUpperCase()} — ${label} (G7, G8)${locator ? ` — ${locator}` : ''}`;
}

function LineRow({ row, index, kind }: { row: BudgetLineDiffRow; index: number; kind: 'changed' | 'added' | 'removed' }) {
  return (
    <tr data-testid={`ver-diff-line-${index}`}>
      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.label.a ?? row.label.b ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.section ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums" title={lineTitle(row, 'a')}>{money(row.unit_cost.a)}</td>
      <td className="px-3 py-2 text-right tabular-nums" title={lineTitle(row, 'b')}>{money(row.unit_cost.b)}</td>
      <td className="px-3 py-2 text-right tabular-nums" title={`delta — ${row.labels.delta ?? 'derived'} (G8)`}>
        {kind === 'changed' ? money(row.unit_cost.delta) : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
        {kind === 'changed' ? pct(row.unit_cost.delta_pct) : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
        {row.overhead_pct ? points(row.overhead_pct.delta_points) : '—'}
      </td>
    </tr>
  );
}

/**
 * `index` is offset by the CALLER so `ver-diff-line-<n>` is one continuous
 * sequence across Changed, then Added, then Removed — never restarting at 0
 * per section (a test locating "the changed row" or "the added row" by
 * index alone must not collide with the other section's own row 0).
 */
function LineTable({ rows, kind, startIndex }: { rows: BudgetLineDiffRow[]; kind: 'changed' | 'added' | 'removed'; startIndex: number }) {
  if (rows.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">None.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Label</th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Section</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">A</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">B</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Delta</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Delta %</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Overhead pts</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row, index) => (
            <LineRow key={`${kind}-${index}`} row={row} index={startIndex + index} kind={kind} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellsDisclosure({ result }: { result: VersionDiffResult }) {
  const [expanded, setExpanded] = useState(false);
  const cells = result.cells;
  if (!cells) return null;
  return (
    <div data-testid="ver-diff-cells">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-2 flex min-h-[44px] items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200 sm:min-h-0"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        cell differences ({cells.total})
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {cells.truncated && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Showing the first {cells.entries.length} of {cells.total} — capped (G9).
            </p>
          )}
          {(cells.sheets_only_in_a.length > 0 || cells.sheets_only_in_b.length > 0) && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {cells.sheets_only_in_a.length > 0 && `Sheets only in A: ${cells.sheets_only_in_a.join(', ')}. `}
              {cells.sheets_only_in_b.length > 0 && `Sheets only in B: ${cells.sheets_only_in_b.join(', ')}.`}
            </p>
          )}
          <div className="max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-3 py-1.5 text-left">Sheet</th>
                  <th className="px-3 py-1.5 text-left">Cell</th>
                  <th className="px-3 py-1.5 text-right">Old</th>
                  <th className="px-3 py-1.5 text-right">New</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {cells.entries.map((entry) => (
                  <tr key={entry.seq}>
                    <td className="px-3 py-1.5">{entry.sheet}</td>
                    <td className="px-3 py-1.5 tabular-nums">{entry.cell_ref}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{entry.old_value ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{entry.new_value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ClauseTable({ result }: { result: VersionDiffResult }) {
  const clauses = result.clauses;
  if (!clauses) return null;
  const rows: Array<{ path: string; heading: string | null; kind: 'added' | 'removed' | 'changed'; excerptA?: string; excerptB?: string }> = [
    ...clauses.changed.map((r) => ({ path: r.path, heading: r.heading.b ?? r.heading.a, kind: 'changed' as const, excerptA: r.excerpt.a, excerptB: r.excerpt.b })),
    ...clauses.added.map((r) => ({ path: r.path, heading: r.heading, kind: 'added' as const, excerptB: r.excerpt })),
    ...clauses.removed.map((r) => ({ path: r.path, heading: r.heading, kind: 'removed' as const, excerptA: r.excerpt })),
  ];
  const KIND_CLS: Record<string, string> = {
    changed: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
    added: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    removed: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <div className="space-y-2">
      {clauses.ambiguous_paths.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Repeated paths (occurrence-numbered, never collapsed): {clauses.ambiguous_paths.join(', ')}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Path</th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Heading</th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((row) => (
              <tr key={`${row.kind}-${row.path}`} data-testid={`ver-diff-clause-${row.path}`}>
                <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{row.path}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.heading ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${KIND_CLS[row.kind]}`}>{row.kind}</span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No clause changes.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{clauses.unchanged} clause(s) unchanged.</p>
    </div>
  );
}

export function DiffView({ result, onDownloadRedline, redlineLoading, redlineError, redlineSummary }: DiffViewProps) {
  return (
    <div data-testid="ver-diff" className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      {result.kind === 'budget' && result.lines && (
        <div className="space-y-3">
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Changed</h4>
            <LineTable rows={result.lines.changed} kind="changed" startIndex={0} />
          </div>
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Added</h4>
            <LineTable rows={result.lines.added} kind="added" startIndex={result.lines.changed.length} />
          </div>
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Removed</h4>
            <LineTable
              rows={result.lines.removed}
              kind="removed"
              startIndex={result.lines.changed.length + result.lines.added.length}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{result.lines.unchanged} line(s) unchanged.</p>
          <CellsDisclosure result={result} />
        </div>
      )}

      {result.kind === 'contract' && result.clauses && (
        <div className="space-y-3">
          <ClauseTable result={result} />
          <div>
            <button
              type="button"
              data-testid="ver-diff-redline"
              onClick={onDownloadRedline}
              disabled={redlineLoading}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <Download size={14} /> {redlineLoading ? 'Preparing redline…' : 'Download the Word redline'}
            </button>
            {redlineError && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400" data-testid="ver-diff-redline-error">
                {redlineError}
              </p>
            )}
            {redlineSummary && (
              <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
                {redlineSummary.changed} changed &middot; {redlineSummary.inserted} inserted &middot; {redlineSummary.deleted} deleted
                {redlineSummary.refused > 0 && (
                  <span className="ml-1 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                    <AlertTriangle size={12} /> {redlineSummary.refused} refused (unread, G9)
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 pt-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
        Not compared: {result.not_compared.join('; ')}
      </div>
    </div>
  );
}

export default DiffView;
